import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const u = localStorage.getItem("arissai_user");
    return u ? JSON.parse(u) : null;
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("arissai_token");
    if (token && !user) {
      api.get("/auth/me").then((res) => {
        setUser(res.data);
        localStorage.setItem("arissai_user", JSON.stringify(res.data));
      }).catch(() => {
        localStorage.removeItem("arissai_token");
        localStorage.removeItem("arissai_user");
      });
    }
  }, []); // eslint-disable-line

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("arissai_token", data.access_token);
      localStorage.setItem("arissai_user", JSON.stringify(data.user));
      setUser(data.user);
      return data.user;
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async (email, password, full_name, industry = "general") => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { email, password, full_name, industry });
      localStorage.setItem("arissai_token", data.access_token);
      localStorage.setItem("arissai_user", JSON.stringify(data.user));
      setUser(data.user);
      return data.user;
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      localStorage.setItem("arissai_user", JSON.stringify(data));
      setUser(data);
      return data;
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("refreshUser failed:", err?.message);
      }
      return null;
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("arissai_token");
    localStorage.removeItem("arissai_user");
    setUser(null);
  }, []);

  const updateUser = useCallback((patch) => {
    setUser((prev) => {
      const next = { ...(prev || {}), ...patch };
      localStorage.setItem("arissai_user", JSON.stringify(next));
      return next;
    });
  }, []);

  // Memoise the context value so consumers don't re-render on every parent update.
  const value = useMemo(
    () => ({ user, loading, login, register, logout, refreshUser, updateUser }),
    [user, loading, login, register, logout, refreshUser, updateUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
